import React from 'react';

/**
 * Custom-styled Checkbox Input Component.
 * Hides the native checkbox and provides a visually consistent, Tailwind-styled alternative.
 * Includes proper labeling and focus states.
 *
 * @param {object} props - Component props.
 * @param {string} props.id - Unique ID for the checkbox and its label.
 * @param {string} props.name - Name attribute for the input.
 * @param {string} [props.label] - Text label for the checkbox.
 * @param {any} [props.value] - Value attribute for the input.
 * @param {boolean} props.checked - Controlled component: current checked state.
 * @param {function} props.onChange - Change event handler.
 * @param {boolean} [props.required=false] - If true, input is required.
 * @param {boolean} [props.disabled=false] - If true, checkbox is disabled.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the wrapper div.
 * @param {string} [props.labelClassName=''] - Additional custom Tailwind classes for the label text.
 */
const CheckboxInput = ({
    id,
    name,
    label,
    value,
    checked,
    onChange,
    required = false,
    disabled = false,
    className = '',
    labelClassName = '',
    ...props
}) => {
    return (
        <div className={`flex items-center mb-4 ${className}`}>
            <input
                id={id}
                name={name}
                type="checkbox"
                value={value}
                checked={checked}
                onChange={onChange}
                required={required}
                disabled={disabled}
                className="sr-only peer" // Visually hide the checkbox but keep it accessible
                {...props}
            />
            <label
                htmlFor={id}
                className={`relative flex items-center cursor-pointer text-text-primary text-sm
                            peer-focus-visible:outline-none peer-focus-visible:ring-2 peer-focus-visible:ring-offset-2 peer-focus-visible:ring-gmail-blue
                            ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${labelClassName}`}
            >
                {/* Custom checkbox visual */}
                <div className={`
                    w-5 h-5 border-2 rounded-md flex items-center justify-center flex-shrink-0
                    transition-all duration-200 ease-in-out
                    ${checked ? 'bg-gmail-blue border-gmail-blue' : 'bg-white border-gray-medium'}
                    peer-hover:border-gmail-blue peer-checked:peer-hover:bg-blue-600
                `}>
                    {checked && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                    )}
                </div>
                {label && <span className="ml-2 select-none">{label}</span>}
            </label>
        </div>
    );
};

export default CheckboxInput;
